import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import {
	createDownloadWorkflow,
	downloadInvestors,
	getInvestorsNamesFiltering,
	getMIPInvestorsData,
	getMIPInvestorsDataForSendModal,
	getMIPInvestorsIdsData,
	getMIPInvestorsOrderedIds,
	getMIPSelectedInvestorsData,
	postMipInvestorsInvitations,
} from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { usePivNames } from '@dealstack/frontend/app/pages/Project/MIP/hooks/usePivNames';
import { useMipClassifiersList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useMipClassifiers';
import {
	InvestorsFilterQuery,
	InvestorsSortingColumn,
	InvestorsSortingQuery,
	ProgrammeStatus,
	SortingOrder,
	InvestorEligibilityFilter,
	InvestorBulkActionDto,
	ClassifierType,
} from '@dealstack/shared/lib/generated';
import { mergeInvestorsFilterQuery } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/utils/mergeInvestorsFilterQuery.ts';
import { isInvestorSyncDownloadLimitError } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/utils/isInvestorSyncDownloadLimitError';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMantineTableDataOperationsFromUrlParamState } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import { SHARES_MODAL_MODE } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/constants';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { isSendDocumentsReturn } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/sendDocumentsRoundTrip.utils';

/**
 * A bulk payload can resolve to investors when it carries explicit ids, a
 * filter/search to query against, or `excludedIds` — the last of which signals
 * greedy "select all across pages" (an empty array means "all, exclude none").
 * `excludedIds: []` is truthy intent, so it must be checked for presence rather
 * than length.
 */
const hasResolvableBulkSelection = (payload: InvestorBulkActionDto): boolean =>
	!!payload.selectedIds?.length ||
	!!payload.filterQuery ||
	!!payload.searchQuery ||
	payload.excludedIds !== undefined;

export const useManageInvestorsController = (isNotInvestorDetailsPage = true) => {
	const { mipId } = useRequiredParams('mipId');
	const queryClient = useQueryClient();

	const postMIPInvestorsInvitationsRequest = useWrappedRequest(postMipInvestorsInvitations);

	const {
		deal,
		mipData,
		refetchDealData,
		disableSendInvestorInvitation,
		vestingTerms,
		hasEditorRole,
		hasExportRole,
		hasKycAdminRole,
	} = useMIPManageContext();

	// Master Documents hands control back here after an "upload document" round trip, with the
	// prior selection stashed in router state — restored via a lazy initialiser (not an effect)
	// so the modal reopens on the same render rather than flashing closed first.
	const location = useLocation();
	const returnedSelection = isSendDocumentsReturn(location.state)
		? location.state.sendDocumentsSelection
		: undefined;

	const [showNewJoinersAndAllocationsModal, setShowNewJoinersAndAllocationsModal] =
		useState(false);
	const [showNewLeaversModal, setShowNewLeaversModal] = useState(false);
	const [investorSharesModalMode, setInvestorSharesModalMode] = useState(
		SHARES_MODAL_MODE.NEW_JOINERS
	);
	const [isSendInvitationModalOpened, setIsSendInvitationModalOpened] = useState(false);
	const [showSendDocumentModal, setShowSendDocumentModal] = useState(Boolean(returnedSelection));
	const [isRequestKycModalOpen, setIsRequestKycModalOpen] = useState(false);

	const [selectedInvestorIdsState, setSelectedInvestorIdsState] = useState(
		returnedSelection?.selectedIds ?? ([] as string[])
	);
	const [filteredInvestorsList, setFilteredInvestorsList] = useState<string[]>([]);
	const [singleInvestorId, setSingleInvestorId] = useState<string | null>();
	const [eligibilityFilter, setEligibilityFilter] = useState<InvestorEligibilityFilter | null>(
		null
	);
	const [bulkActionPayload, setBulkActionPayload] = useState<InvestorBulkActionDto | null>(null);

	const mantineTableDataOperations = useMantineTableDataOperationsFromUrlParamState({
		initialQuickFilters: {
			programmeStatuses: `${ProgrammeStatus.JOINER},${ProgrammeStatus.HOLDER}`,
		},
	});

	// Investor-name filter options load lazily (only once the filter dropdown is
	// opened) and search/paginate server-side, so the page no longer fetches every
	// investor name on mount.
	const [investorFilterEnabled, setInvestorFilterEnabled] = useState(false);
	const [investorNameSearch, setInvestorNameSearch] = useState('');

	const investorNamesQuery = useInfiniteQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_NAMES_FILTERING_GET, { searchQuery: investorNameSearch }],

		initialPageParam: '',
		queryFn: async ({ pageParam }) =>
			await getInvestorsNamesFiltering(API_PATHS.MIP_INVESTOR_NAMES_FILTERING_GET, {
				cursor: pageParam,
				searchQuery: investorNameSearch || undefined,
				limit: 100,
			}),

		getNextPageParam: (lastPage) => lastPage.payload?.nextCursor,
		placeholderData: keepPreviousData,
		enabled: investorFilterEnabled,
	});

	const investorFilterAsync = useMemo(
		() => ({
			serverSide: true,
			onDropdownOpen: () => setInvestorFilterEnabled(true),
			onSearchChange: setInvestorNameSearch,
			onReachEnd: () => {
				if (investorNamesQuery.hasNextPage && !investorNamesQuery.isFetchingNextPage) {
					void investorNamesQuery.fetchNextPage();
				}
			},
			isLoading: investorNamesQuery.isFetching,
			hasMore: !!investorNamesQuery.hasNextPage,
		}),
		[
			investorNamesQuery.hasNextPage,
			investorNamesQuery.isFetchingNextPage,
			investorNamesQuery.isFetching,
			investorNamesQuery.fetchNextPage,
		]
	);

	const investorFilterOptions = useMemo(
		() =>
			investorNamesQuery.data?.pages?.flatMap(
				(page) =>
					page?.payload?.investors.map(({ id, person }) => ({
						label: person.displayName,
						value: id,
					})) ?? []
			) ?? [],
		[investorNamesQuery.data]
	);

	// PIV person names for the multi-select filter — same lazy + server-driven
	// treatment as the investor-name filter above.
	const [pivFilterEnabled, setPivFilterEnabled] = useState(false);
	const [pivNameSearch, setPivNameSearch] = useState('');

	const pivNamesQuery = usePivNames({
		mipId,
		searchQuery: pivNameSearch || undefined,
		limit: 100,
		enabled: pivFilterEnabled,
	});

	const pivFilterAsync = useMemo(
		() => ({
			serverSide: true,
			onDropdownOpen: () => setPivFilterEnabled(true),
			onSearchChange: setPivNameSearch,
			onReachEnd: () => {
				if (pivNamesQuery.hasNextPage && !pivNamesQuery.isFetchingNextPage) {
					void pivNamesQuery.fetchNextPage();
				}
			},
			isLoading: pivNamesQuery.isFetching,
			hasMore: !!pivNamesQuery.hasNextPage,
		}),
		[
			pivNamesQuery.hasNextPage,
			pivNamesQuery.isFetchingNextPage,
			pivNamesQuery.isFetching,
			pivNamesQuery.fetchNextPage,
		]
	);

	const pivFilterOptions = useMemo(
		() =>
			pivNamesQuery.data?.pages?.flatMap(
				(page) =>
					page?.payload?.names.map(({ id, displayName }) => ({
						label: displayName ?? '',
						value: id,
					})) ?? []
			) ?? [],
		[pivNamesQuery.data]
	);

	const { data: mipClassifiers } = useMipClassifiersList({
		mipId: mipId ?? '',
		isArchived: false,
		classifiedType: ClassifierType.INVESTOR_COHORT,
		enabled: !!mipId,
	});

	const mipClassifierFilterOptions = useMemo(
		() => mipClassifiers?.map((c) => ({ label: c.name, value: c.id })) ?? [],
		[mipClassifiers]
	);

	const mergedFilterQuery = useMemo(
		() =>
			mergeInvestorsFilterQuery(
				mantineTableDataOperations.quickFilters,
				mantineTableDataOperations.columnFilters,
				eligibilityFilter
			),
		[
			mantineTableDataOperations.quickFilters,
			mantineTableDataOperations.columnFilters,
			eligibilityFilter,
		]
	);

	// When quick filter for programmeStatuses changes, clear the programmeStatus column filter
	useEffect(() => {
		if (mantineTableDataOperations.quickFilters.programmeStatuses) {
			mantineTableDataOperations.setColumnFilters((prev) =>
				prev.filter((cf) => cf.id !== 'programmeStatus')
			);
		}
	}, [mantineTableDataOperations.quickFilters.programmeStatuses]);

	// When programmeStatus column filter is set, clear the quick filter for programmeStatuses
	useEffect(() => {
		const cf = mantineTableDataOperations.columnFilters.find((f) => f.id === 'programmeStatus');
		if (cf && Array.isArray(cf.value) && cf.value.length > 0) {
			mantineTableDataOperations.setQuickFilters((prev) => {
				// eslint-disable-next-line unused-imports/no-unused-vars
				const { programmeStatuses, ...rest } = prev;
				return rest;
			});
		}
	}, [mantineTableDataOperations.columnFilters]);

	// Reset pagination to page 0 when server-side filters or search change
	const prevFilterQueryRef = useRef<InvestorsFilterQuery | undefined>();
	const prevSearchRef = useRef<string | undefined>();
	useEffect(() => {
		const filterChanged =
			prevFilterQueryRef.current !== undefined &&
			JSON.stringify(prevFilterQueryRef.current) !== JSON.stringify(mergedFilterQuery);
		const searchChanged =
			prevSearchRef.current !== undefined &&
			prevSearchRef.current !== mantineTableDataOperations.globalSearchString;

		if (
			(filterChanged || searchChanged) &&
			mantineTableDataOperations.pagination.pageIndex !== 0
		) {
			mantineTableDataOperations.setPagination((prev) => ({ ...prev, pageIndex: 0 }));
		}
		prevFilterQueryRef.current = mergedFilterQuery;
		prevSearchRef.current = mantineTableDataOperations.globalSearchString;
	}, [mergedFilterQuery, mantineTableDataOperations.globalSearchString]);

	const columnIdToSortingColumn: Record<string, InvestorsSortingColumn> = useMemo(
		() => ({
			investor: InvestorsSortingColumn.INVESTOR,
			accountStatus: InvestorsSortingColumn.LAST_LOGIN,
			investmentVehicles: InvestorsSortingColumn.INVESTMENT_VEHICLE,
			programmeHoldings: InvestorsSortingColumn.TOTAL_HOLDINGS,
			citizenships: InvestorsSortingColumn.CITIZENSHIP,
			employeeIds: InvestorsSortingColumn.EMPLOYEE_ID,
		}),
		[]
	);

	const sortingQuery = useMemo((): InvestorsSortingQuery => {
		const [first] = mantineTableDataOperations.sorting;
		if (!first || !columnIdToSortingColumn[first.id]) {
			return { sortingColumn: undefined, sortingOrder: undefined };
		}
		return {
			sortingColumn: columnIdToSortingColumn[first.id],
			sortingOrder: first.desc ? SortingOrder.DESC : SortingOrder.ASC,
		};
	}, [mantineTableDataOperations.sorting, columnIdToSortingColumn]);

	const getMIPInvestorsV2DataRequest = useQuery({
		queryKey: [
			API_PATHS.MIP_INVESTOR_LIST_POST,
			{
				offset:
					mantineTableDataOperations.pagination.pageIndex *
					mantineTableDataOperations.pagination.pageSize,
				limit: mantineTableDataOperations.pagination.pageSize,
				filterQuery: mergedFilterQuery,
				sortingQuery,
				searchQuery: mantineTableDataOperations.globalSearchString || undefined,
			},
		],

		queryFn: ({ signal }) =>
			getMIPInvestorsData(
				API_PATHS.MIP_INVESTOR_LIST_POST,
				{
					offset:
						mantineTableDataOperations.pagination.pageIndex *
						mantineTableDataOperations.pagination.pageSize,
					limit: mantineTableDataOperations.pagination.pageSize,
					filterQuery: mergedFilterQuery,
					sortingQuery,
					searchQuery: mantineTableDataOperations.globalSearchString || undefined,
				},
				signal
			),
		placeholderData: keepPreviousData,
		enabled: isNotInvestorDetailsPage,
	});

	const navigationQuery = {
		filterQuery: mergedFilterQuery,
		sortingQuery,
		searchQuery: mantineTableDataOperations.globalSearchString || undefined,
	};
	const getMIPInvestorsOrderedIdsRequest = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_ORDERED_IDS_POST, navigationQuery],
		queryFn: ({ signal }) =>
			getMIPInvestorsOrderedIds(
				API_PATHS.MIP_INVESTOR_ORDERED_IDS_POST,
				navigationQuery,
				signal
			),
		enabled: !isNotInvestorDetailsPage,
	});

	const getMIPInvestorsIdsDataRequest = useQuery({
		queryKey: [
			API_PATHS.MIP_INVESTOR_IDS_POST,
			{
				filterQuery: mergedFilterQuery,
			},
		],

		queryFn: ({ signal }) =>
			getMIPInvestorsIdsData(
				API_PATHS.MIP_INVESTOR_IDS_POST,
				{
					filterQuery: mergedFilterQuery,
				},
				signal
			),
		enabled: isNotInvestorDetailsPage,
	});

	const selectedInvestorsBulkPayload: InvestorBulkActionDto = bulkActionPayload ?? {
		selectedIds: selectedInvestorIdsState,
	};

	const getMIPSelectedInvestorsDataRequest = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_SELECTED_INVESTORS_POST, selectedInvestorsBulkPayload],
		queryFn: ({ signal }) =>
			getMIPSelectedInvestorsData(
				API_PATHS.MIP_INVESTOR_SELECTED_INVESTORS_POST,
				selectedInvestorsBulkPayload,
				signal
			),

		enabled: hasResolvableBulkSelection(selectedInvestorsBulkPayload),
	});

	const selectedInvestors = getMIPSelectedInvestorsDataRequest?.data?.payload?.investors ?? [];

	const investorsIdIsEligibleForInvitationSent = useMemo(
		() => selectedInvestors.map((item) => item.id),
		[selectedInvestors]
	);

	// A row-level invite targets a single investor but still resolves through the
	// send-modal endpoint, so the modal reads contract names from one query whatever
	// opened it.
	const sendModalBulkPayload: InvestorBulkActionDto = singleInvestorId
		? { selectedIds: [singleInvestorId] }
		: bulkActionPayload ?? { selectedIds: investorsIdIsEligibleForInvitationSent };

	const getMIPInvestorsDataForSendModalRequest = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_INVITE_INVESTORS_DATA_POST, sendModalBulkPayload],
		queryFn: ({ signal }) =>
			getMIPInvestorsDataForSendModal(
				API_PATHS.MIP_INVESTOR_INVITE_INVESTORS_DATA_POST,
				sendModalBulkPayload,
				signal
			),

		enabled: isSendInvitationModalOpened && hasResolvableBulkSelection(sendModalBulkPayload),
	});

	const investorsDataForSendModal =
		getMIPInvestorsDataForSendModalRequest?.data?.payload?.investors;

	const investors = useMemo(
		() => getMIPInvestorsV2DataRequest?.data?.payload?.investors ?? [],
		[getMIPInvestorsV2DataRequest?.data?.payload]
	);
	const stats = getMIPInvestorsV2DataRequest?.data?.payload?.stats;
	const customDetailColumns = useMemo(
		() => getMIPInvestorsV2DataRequest?.data?.payload?.customDetailColumns ?? [],
		[getMIPInvestorsV2DataRequest?.data?.payload]
	);

	const downloadInvestorsRequest = useMutation({
		mutationFn: async (payload: InvestorBulkActionDto): Promise<Blob | null> => {
			try {
				return await downloadInvestors(payload);
			} catch (error) {
				if (isInvestorSyncDownloadLimitError(error)) {
					return null;
				}

				throw error;
			}
		},
		onSuccess: (payload) => {
			if (!payload) {
				return;
			}
			const fileName = `${deal?.name ?? 'unknown'}-${mipData?.entityName ?? ''}-investors.xlsx`;
			downloadFileFromBlob(payload, fileName);
		},
	});

	const createDownloadWorkflowRequest = useMutation({
		mutationFn: createDownloadWorkflow,
	});

	// Both exports run through the async Temporal workflow (MIP-2838). "Download All"
	// passes an empty payload, which the backend resolves to every investor in the MIP
	// (see `bulkActionRequestsAllInvestors`); the bulk-actions "Download" passes the
	// table's filter/selection payload.
	const startInvestorsDownloadWorkflow = useCallback(
		(payload: InvestorBulkActionDto) => {
			createDownloadWorkflowRequest.mutate(payload);
		},
		[createDownloadWorkflowRequest.mutate]
	);

	const refetchInvestorsList = getMIPInvestorsV2DataRequest.refetch;
	const refetchInvestorsIds = getMIPInvestorsIdsDataRequest.refetch;

	const refreshData = useCallback(() => {
		void refetchDealData();

		if (isNotInvestorDetailsPage) {
			void refetchInvestorsList();
			void refetchInvestorsIds();
		}
		void queryClient.invalidateQueries({
			queryKey: [API_PATHS.MIP_INVESTOR_SELECTED_INVESTORS_POST],
		});
	}, [
		refetchDealData,
		isNotInvestorDetailsPage,
		refetchInvestorsList,
		refetchInvestorsIds,
		queryClient,
	]);

	useEffect(() => {
		if (mipId) {
			refreshData();
		}
	}, [mipId]);

	const setQuickFilters = mantineTableDataOperations.setQuickFilters;
	const setColumnFilters = mantineTableDataOperations.setColumnFilters;

	const refreshDataWithoutProgrammeFilter = useCallback(() => {
		setQuickFilters((prev) => {
			// eslint-disable-next-line unused-imports/no-unused-vars
			const { programmeStatuses, ...rest } = prev;
			return rest;
		});
		setColumnFilters((prev) => prev.filter((cf) => cf.id !== 'programmeStatus'));
		refreshData();
	}, [setQuickFilters, setColumnFilters, refreshData]);

	const onDownloadInvestors = useCallback(
		async (payload: InvestorBulkActionDto): Promise<boolean> =>
			(await downloadInvestorsRequest.mutateAsync(payload)) !== null,
		[downloadInvestorsRequest.mutateAsync]
	);

	const investorsToSendInvitation = useMemo(
		() => investorsDataForSendModal ?? [],
		[investorsDataForSendModal]
	);

	const orderedInvestorIds = useMemo(
		() => getMIPInvestorsOrderedIdsRequest.data?.payload?.ids ?? [],
		[getMIPInvestorsOrderedIdsRequest.data]
	);

	return {
		refreshData,
		refreshDataWithoutProgrammeFilter,
		postMIPInvestorsInvitationsRequest,
		getMIPInvestorsV2DataRequest,
		orderedInvestorIds,
		investors,
		customDetailColumns,
		allInvestorsIds: getMIPInvestorsIdsDataRequest?.data?.payload ?? [],
		totalInvestorsNumber: getMIPInvestorsV2DataRequest.data?.payload?.stats?.investorCount ?? 0,
		resolvedRecipientsCount:
			getMIPSelectedInvestorsDataRequest?.data?.payload?.totalRecord ?? 0,
		onDownloadInvestors,
		downloadInvestorsRequest,
		selectedInvestorIdsState,
		setSelectedInvestorIdsState,
		selectedInvestors,
		setFilteredInvestorsList,
		setSingleInvestorId,
		singleInvestorId,
		filteredInvestorsList,
		investorsToSendInvitation,
		isLoading: getMIPInvestorsV2DataRequest?.isLoading,
		isFetching: getMIPInvestorsV2DataRequest?.isFetching,
		disableSendInvestorInvitation,
		mipData,
		showNewJoinersAndAllocationsModal,
		setShowNewJoinersAndAllocationsModal,
		showNewLeaversModal,
		setShowNewLeaversModal,
		investorSharesModalMode,
		setInvestorSharesModalMode,
		isSendInvitationModalOpened,
		setIsSendInvitationModalOpened,
		showSendDocumentModal,
		setShowSendDocumentModal,
		isRequestKycModalOpen,
		setIsRequestKycModalOpen,
		getMIPSelectedInvestorsDataRequest,
		getMIPInvestorsDataForSendModalRequest,
		startInvestorsDownloadWorkflow,
		createDownloadWorkflowRequest,
		vestingTerms,
		stats,
		mantineTableDataOperations,
		mergedFilterQuery,
		bulkActionPayload,
		setBulkActionPayload,
		investorFilterOptions,
		investorFilterAsync,
		pivFilterOptions,
		pivFilterAsync,
		mipClassifierFilterOptions,
		hasEditorRole,
		hasExportRole,
		hasKycAdminRole,
		setEligibilityFilter,
	};
};
